import React from 'react';
import memoize from 'lodash/memoize';
import { Field } from '@atlaskit/form';
import { AsyncSelect } from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import TextField from '@atlaskit/textfield';
import UserPicker, { OptionData, OnChange } from '@atlaskit/user-picker';
import { useQuery } from '@tanstack/react-query';

import { FormPayload } from '@/ui';
import { useAutoCompleteController, useFetchErrorController } from '@/controllers';
import { formatOptionLabel, FormFieldTooltipLabel } from '@/components/primitive';
import { AVATARS_BASE_URL, MAX_TEXT_LENGTH } from '@/constants';
import { apiResolve, apis } from '@/services';
import { ConditionalErrorMessage } from '@/components/composed/forms/form-errors';

import { helperCopy } from '../copy';

import CapabilityTierField from './fields/capability-tier';

// in the absence of a full name to display, we present the username instead
function defaultUsernameToOwner(username: string): OptionData | undefined {
  if (username) {
    return {
      avatarUrl: `${AVATARS_BASE_URL}/${username}`,
      id: username,
      name: username,
      type: 'user',
    };
  }
  return undefined;
}

interface IProps {
  isLoading: boolean;
}

type CapabilityFormProps = IProps & Partial<FormPayload<API.Capability>>;

const CapabilityForm: React.FC<CapabilityFormProps> = ({
  isLoading,
  name = '',
  description = '',
  team = null,
  product = null,
  upstreamDependencies = [],
  owner: currentOwnerUsername = '',
  importance,
}) => {
  const loadTeams = useAutoCompleteController('team');
  const loadProducts = useAutoCompleteController('product');
  const loadCapabilities = useAutoCompleteController('capability');
  const loadUsers = useAutoCompleteController('user');
  const loadAndMapUsers = React.useCallback(
    memoize((searchText?: string) =>
      loadUsers(searchText).then(users => {
        const options: OptionData[] = users.map((u, index) => ({
          avatarUrl: `${AVATARS_BASE_URL}/${u.publicId}`,
          id: u.publicId || `user_${u.name || index}`,
          name: u.name || '',
          type: 'user',
        }));
        return options;
      })
    ),
    [loadUsers]
  );

  const validateName = React.useCallback(
    memoize((value?: string) => {
      if (!value) {
        return 'Don\'t forget to name your Capability.';
      }

      // existing name is fine
      if (value === name) {
        return undefined;
      }

      return loadCapabilities(value).then(options => {
        if (
          options.find(
            ({ name: capabilityName }) => capabilityName?.toLowerCase() === value.toLowerCase()
          )
        ) {
          return 'Name already in use';
        }

        return undefined;
      });
    }),
    [name, loadCapabilities]
  );

  const [currentOwner, setCurrentOwner] = React.useState<OptionData | undefined>(
    defaultUsernameToOwner(currentOwnerUsername)
  );
  const invalidOwnerError = React.useMemo(
    () => ({
      title: 'Current owner is invalid',
      message: `We couldn't find an Atlassian with username "${currentOwnerUsername}" even
        though they're listed as the owner of this capability. Perhaps their
        account is deactivated or they've changed their username? You won't be
        able to save changes to this capability until you select a valid
        owner.`,
    }),
    [currentOwnerUsername]
  );

  const { onError: setGlobalError } = useFetchErrorController();
  const { data, isLoading: isCurrentOwnerLoading } = useQuery(
    ['username', currentOwnerUsername],
    () => apiResolve(apis.user.getByUsername({ username: currentOwnerUsername })),
    {
      onError: (_err: Error) => setGlobalError(invalidOwnerError),
      enabled: !!currentOwnerUsername,
    }
  );

  React.useEffect(() => {
    setCurrentOwner(
      data
        ? {
            avatarUrl: `${AVATARS_BASE_URL}/${data.username}`,
            id: data.username,
            name: data.displayName,
            type: 'user',
          }
        : defaultUsernameToOwner(currentOwnerUsername)
    );
  }, [data, currentOwnerUsername]);

  return (
    <>
      <Field<string>
        name="name"
        label={
          <FormFieldTooltipLabel isRequired label="Name" content={helperCopy.capability.name} />
        }
        validate={validateName}
        isRequired
        defaultValue={name}
        isDisabled={isLoading}
      >
        {({ fieldProps, error }) => (
          <>
            <TextField
              maxLength={MAX_TEXT_LENGTH}
              placeholder="Name of the Capability, e.g. Site Admin Access"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...fieldProps}
            />
            <ConditionalErrorMessage error={error} />
          </>
        )}
      </Field>
      <Field<any> name="team" label="Team" isRequired isDisabled={isLoading} defaultValue={team}>
        {({ fieldProps: { id, ...rest }, error }) => (
          <>
            <AsyncSelect
              formatOptionLabel={formatOptionLabel}
              placeholder="A Team, e.g. Observability, Identity"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...rest}
              inputId={id}
              defaultOptions
              loadOptions={loadTeams}
              validationState={error ? 'error' : 'default'} // https://product-fabric.atlassian.net/browse/DSP-821
            />
            <ConditionalErrorMessage error={error} />
          </>
        )}
      </Field>
      <Field<any>
        name="product"
        label="Product"
        isRequired
        defaultValue={product}
        isDisabled={isLoading}
      >
        {({ fieldProps: { id, ...rest }, error }) => (
          <>
            <AsyncSelect
              formatOptionLabel={formatOptionLabel}
              placeholder="A Product, e.g. Jira Align, Pollinator"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...rest}
              inputId={id}
              defaultOptions
              loadOptions={loadProducts}
              validationState={error ? 'error' : 'default'} // https://product-fabric.atlassian.net/browse/DSP-821
            />
            <ConditionalErrorMessage error={error} />
          </>
        )}
      </Field>
      <Field<any>
        name="upstreamDependencies"
        label={
          <FormFieldTooltipLabel
            label="Upstream Dependencies"
            content={helperCopy.capability.upstreamDependencies}
          />
        }
        defaultValue={upstreamDependencies}
        isDisabled={isLoading}
      >
        {({ fieldProps: { id, ...rest }, error }) => (
          <>
            <AsyncSelect
              formatOptionLabel={formatOptionLabel}
              placeholder="Upstream Capabilities, e.g. View Issue"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...rest}
              isMulti
              inputId={id}
              defaultOptions
              loadOptions={loadCapabilities}
              validationState={error ? 'error' : 'default'} // https://product-fabric.atlassian.net/browse/DSP-821
            />
            <ConditionalErrorMessage error={error} />
          </>
        )}
      </Field>
      <Field<OptionData>
        name="owner"
        label={
          <FormFieldTooltipLabel isRequired label="Owner" content={helperCopy.capability.owner} />
        }
        isRequired
        validate={value => (!value || !value.id ? 'Value required' : undefined)}
        defaultValue={currentOwner}
        isDisabled={isLoading}
      >
        {({ fieldProps, error }) => (
          <>
            <UserPicker
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...fieldProps}
              fieldId={null}
              inputId={fieldProps.id}
              loadOptions={loadAndMapUsers}
              onChange={fieldProps.onChange as OnChange}
              // show loading symbol when loading the initial owner (when
              // editing). NOTE: This does not affect whether the loading symbol
              // is displayed while a user is typing - that's internal to the
              // component no matter what.
              isLoading={isCurrentOwnerLoading}
              placeholder="An Atlassian's name or username, e.g. sfarquhar"
              width="100%"
            />
            <ConditionalErrorMessage error={error} />
          </>
        )}
      </Field>
      <Field<string, HTMLTextAreaElement>
        name="description"
        label="Description"
        defaultValue={description}
      >
        {({ fieldProps, error }) => (
          <>
            {/* @ts-ignore */}
            <TextArea maxLength={MAX_TEXT_LENGTH} minimumRows={2} {...fieldProps} />
            <ConditionalErrorMessage error={error} />
          </>
        )}
      </Field>
      <CapabilityTierField importance={importance} />
    </>
  );
};

export default CapabilityForm;
